import { useCallback, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { UploadIcon, XIcon } from "lucide-react";
import { Input } from "../../../ui/input";
import { cn } from "../../../../lib/utils";

interface UploadInputProps {
    value?: File | null;
    onChange?: (file: File | null) => void;
    accept?: string;
    disabled?: boolean;
    className?: string;
}

function UploadInput({ value, onChange, accept, disabled, className }: UploadInputProps) {
    const { t } = useTranslation();

    const onFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e.target.files?.[0] ?? null);
    }, [onChange]);

    if (value) {
        return (
            <div className={cn("tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-border tw-border-input tw-px-3 tw-py-2", className)}>
                <span className="tw-truncate tw-text-sm">{value.name}</span>
                <XIcon
                    className="tw-size-4 tw-shrink-0 tw-cursor-pointer tw-text-red-500"
                    onClick={() => !disabled && onChange?.(null)}
                />
            </div>
        );
    }

    return (
        <label
            className={cn(
                "tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-border-dashed tw-border-input tw-py-8 tw-text-muted-foreground hover:tw-bg-accent/50",
                disabled && "tw-pointer-events-none tw-opacity-50",
                className
            )}
        >
            <UploadIcon className="tw-size-6" />
            <span className="tw-text-sm">{t("upload-file")}</span>
            <Input
                type="file"
                accept={accept}
                disabled={disabled}
                className="tw-hidden"
                onChange={onFileChange}
            />
        </label>
    );
}

export default UploadInput;
